import type { SupabaseClient } from '@supabase/supabase-js';
import { db } from '$db/local/db';
import { nowIso } from '../repositories/common';
import { quarantineOperation } from '../repositories/quarantine.repo';
import { sortOpsForPush } from './order';
import { hydrateForPush, type HydratedOp } from './hydrate';
import { runCoalescence } from '../repositories/outbox.repo';

// Aumentamos el tamaño del lote a 500 para reducir peticiones a Supabase
export const PUSH_BATCH_SIZE = 500;

export interface RpcResultItem {
  idempotency_key: string;
  status: 'applied' | 'skipped' | 'error';
  error?: string;
}

export interface PushResult {
  attempted: number;
  applied: number;
  skipped: number;
  quarantined: number;
  networkError: string | null;
}

export async function pushPendingOps(
  client: SupabaseClient,
  userId: string
): Promise<PushResult> {
  const result: PushResult = {
    attempted: 0,
    applied: 0,
    skipped: 0,
    quarantined: 0,
    networkError: null
  };

  // 0. VERIFICAR SI OTRO DISPOSITIVO BORRÓ LOS DATOS (Tombstone)
  try {
    const { data: syncState } = await client
      .from('user_sync_state')
      .select('last_wipe_at')
      .eq('user_id', userId)
      .maybeSingle();

    if (syncState?.last_wipe_at) {
      const remoteWipeAt = new Date(syncState.last_wipe_at).getTime();
      const localWipeAt = Number(localStorage.getItem('sq:last_seen_wipe_at') || 0);

      if (remoteWipeAt > localWipeAt) {
        console.warn('[Sync] 🚨 Remote wipe detected! Clearing local DB to prevent resurrection.');
        
        // 1. Guardamos la fecha para no repetir el proceso
        localStorage.setItem('sq:last_seen_wipe_at', String(remoteWipeAt));
        
        // 2. Destruimos la base de datos local
        await db.delete();
        
        // 3. Recargamos la página para que arranque limpia y se sincronice con la nada
        window.location.reload();
        
        // 4. Abortamos el push
        return result; 
      }
    }
  } catch (e) {
    console.warn('[Sync] Failed to check for remote wipe, proceeding with sync:', e);
  }

  // 1. Ejecutar Coalescence para agrupar/descartar operaciones redundantes
  try {
    await runCoalescence();
  } catch (e) {
    console.warn('[Sync] Coalescence failed, proceeding with raw ops:', e);
  }

  // 2. Leer la cola
  const all = await db.outbox.where('userId').equals(userId).toArray();
  const pending = all
    .filter((o) => o.status === 'pending')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .slice(0, PUSH_BATCH_SIZE);

  result.attempted = pending.length;
  if (pending.length === 0) return result;

  const sorted = sortOpsForPush(pending);
  const hydrated: HydratedOp[] = sorted.map(hydrateForPush);

  // 3. Marcar como 'syncing'
  await db.transaction('rw', db.outbox, async () => {
    for (const op of sorted) {
      await db.outbox.update(op.id, { status: 'syncing', updatedAt: nowIso() });
    }
  });

  if (import.meta.env.DEV) {
    console.info('[StudyQuantifier] push → RPC apply_sync_batch', {
      opCount: hydrated.length,
      sample: hydrated[0]
    });
  }

  // 4. Ejecutar RPC con Backoff Exponencial
  let finalData: RpcResultItem[] | null = null;
  let finalError: any = null;
  let attempt = 0;
  const maxRetries = 3;
  const baseDelayMs = 1000;
  const maxDelayMs = 8000;

  while (attempt < maxRetries) {
    const { data, error } = await client.rpc('apply_sync_batch', {
      operations: hydrated
    });

    if (!error) {
      finalData = data as RpcResultItem[];
      finalError = null;
      break;
    }

    // Si es 429 (Too Many Requests) o 5xx, reintentar con backoff
    if (error.code === '429' || (error.code && error.code.startsWith('5'))) {
      attempt++;
      if (attempt >= maxRetries) {
        finalError = error;
        break;
      }
      const exponential = baseDelayMs * Math.pow(2, attempt - 1);
      const capped = Math.min(exponential, maxDelayMs);
      const jitterDelay = Math.random() * capped;
      
      console.warn(`[Sync] RPC failed with ${error.code}. Retrying in ${Math.round(jitterDelay)}ms... (Attempt ${attempt}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, jitterDelay));
    } else {
      // Error no recuperable (400, etc.)
      finalError = error;
      break;
    }
  }

  if (import.meta.env.DEV) {
    console.info('[StudyQuantifier] push ← RPC response', { data: finalData, error: finalError });
  }

  // 5. Manejar el resultado final
  if (finalError) {
    await revertToPending(sorted.map((o) => o.id));
    result.networkError = finalError.message;
    return result;
  }

  const byKey = new Map<string, string>();
  for (const op of sorted) {
    byKey.set(op.idempotencyKey, op.id);
  }

  const items = (finalData ?? []) as RpcResultItem[];
  const toDelete: string[] = [];

  for (const item of items) {
    const opId = byKey.get(item.idempotency_key);
    if (!opId) continue;

    if (item.status === 'applied' || item.status === 'skipped') {
      toDelete.push(opId);
      if (item.status === 'applied') result.applied += 1;
      else result.skipped += 1;
      continue;
    }

    const op = sorted.find((o) => o.id === opId);
    if (op) {
      await quarantineOperation({
        entityType: op.entityType,
        entityId: op.entityId,
        op: op.op,
        payload: op.payload,
        schemaVersion: op.schemaVersion ?? 1,
        reason: `rpc_error: ${item.error ?? 'unknown'}`,
        bytes: op.bytes
      });
    }
    await db.outbox.delete(opId);
    result.quarantined += 1;
  }

  if (toDelete.length > 0) {
    await db.outbox.bulkDelete(toDelete);
  }

  return result;
}

async function revertToPending(opIds: string[]): Promise<void> {
  if (opIds.length === 0) return;
  const now = nowIso();
  await db.transaction('rw', db.outbox, async () => {
    for (const id of opIds) {
      await db.outbox.update(id, { status: 'pending', updatedAt: now });
    }
  });
}